<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import type { DiffFilterType } from '../../types/TextDiffsTypes';

const props = defineProps({
    activeFilters: {
        type: Array as () => DiffFilterType[],
        required: true,
    },
});

const emit = defineEmits(['toggleFilter']);

const { t } = useI18n();

const legendItems = computed(() => [
    {
        type: 'unchanged' as DiffFilterType,
        label: t('textDiffs.unchanged'),
        colorClass: 'legend-unchanged',
        dataCy: 'text-diffs_legend-unchanged'
    },
    {
        type: 'changed' as DiffFilterType,
        label: t('textDiffs.changed'),
        colorClass: 'legend-changed',
        dataCy: 'text-diffs_legend-changed'
    },
    {
        type: 'added' as DiffFilterType,
        label: t('textDiffs.added'),
        colorClass: 'legend-added',
        dataCy: 'text-diffs_legend-added'
    },
    {
        type: 'removed' as DiffFilterType,
        label: t('textDiffs.removed'),
        colorClass: 'legend-removed',
        dataCy: 'text-diffs_legend-removed'
    }
]);

function toggleFilter(filterType: DiffFilterType) {
    emit('toggleFilter', filterType);
}

function isActive(filterType: DiffFilterType): boolean {
    return props.activeFilters.includes(filterType);
}
</script>

<template>
    <div class="diff-legend" data-cy="text-diffs_legend">
        <span class="filter-label">{{ t('textDiffs.filter') }}:</span>
        <div class="legend-items">
            <button
                v-for="item in legendItems"
                :key="item.type"
                class="legend-item"
                :class="{ 'legend-item--active': isActive(item.type), 'legend-item--inactive': !isActive(item.type) && activeFilters.length > 0 }"
                :data-cy="item.dataCy"
                @click="toggleFilter(item.type)"
                type="button"
            >
                <span class="legend-color" :class="item.colorClass"></span>
                {{ item.label }}
            </button>
        </div>
    </div>
</template>

<style scoped>
.diff-legend {
    display: flex;
    align-items: center;
    gap: 1rem;
    flex-wrap: wrap;
}

.filter-label {
    font-weight: 600;
    font-size: 0.875rem;
    color: var(--p-text-color);
}

.legend-items {
    display: flex;
    gap: 1rem;
    flex-wrap: wrap;
}

.legend-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.875rem;
    background: none;
    border: none;
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s ease;
}

.legend-item:hover {
    background-color: var(--p-primary-50);
    color: var(--p-primary-700);
}

.legend-item--active {
    background-color: var(--p-primary-50);
    color: var(--p-primary-700);
    font-weight: 600;
}

.legend-item--inactive {
    opacity: 0.4;
}

.legend-color {
    width: 16px;
    height: 16px;
    border-radius: 2px;
    border: 1px solid;
}

.legend-unchanged {
    background-color: transparent;
    border-color: var(--p-surface-border);
}

.legend-changed {
    background-color: var(--diff-changed-bg);
    border-color: var(--diff-changed-border);
}

.legend-added {
    background-color: var(--diff-added-bg);
    border-color: var(--diff-added-border);
}

.legend-removed {
    background-color: var(--diff-removed-bg);
    border-color: var(--diff-removed-border);
}

@media (max-width: 768px) {
    .diff-legend {
        justify-content: center;
    }
}
</style>
